import { ScrollView, StyleSheet, View } from 'react-native'
import { Icon, Modal, Portal, Text, useTheme } from 'react-native-paper'
import { MOONLIGHT_COLOR } from '@/constants/monitor-status'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useDismissOnEscape } from '@/hooks/useDismissOnEscape'
import { getMarkerVisual } from '@/lib/monitor/markerVisual'
import type { UserMarkerVariant } from '@/types/mapData'
import { Button } from '@/ui/Button'

interface MonitorLegendProps {
    visible: boolean
    onDismiss: () => void
}

const MARKER_SIZE = 32
// A representative user color so legend swatches feel like the real cards
// without picking from any one user's assigned color.
const SAMPLE_USER_COLOR = '#2563eb'

interface MarkerEntry {
    variant: UserMarkerVariant
    movementIcon: string
    title: string
    description: string
}

const MARKER_ENTRIES: MarkerEntry[] = [
    {
        variant: 'moving',
        movementIcon: 'truck',
        title: 'Moving',
        description: 'Clocked in and on the move — driving, walking, or otherwise traveling.',
    },
    {
        variant: 'on_site',
        movementIcon: 'map-marker',
        title: 'On site',
        description: "Inside the geofence of the job they're clocked into.",
    },
    {
        variant: 'idle',
        movementIcon: 'alert-circle',
        title: 'Needs attention',
        description:
            'Stale GPS, off-site while clocked in, or pinging during work hours without a job — the dashed ring flags something to review.',
    },
    {
        variant: 'off',
        movementIcon: 'walk',
        title: 'Outside hours',
        description: 'Outlined marker — not on the schedule right now.',
    },
]

interface EntryIconRow {
    icon: string
    title: string
    description: string
    color: 'info' | 'success' | 'warning' | 'neutral' | 'mutedText' | 'moonlight'
}

const ENTRY_ICON_ROWS: EntryIconRow[] = [
    {
        icon: 'clock-outline',
        title: 'Timer',
        description: 'Clocked in via the in-app timer.',
        color: 'info',
    },
    {
        icon: 'map-marker-radius',
        title: 'Geofence',
        description: 'Auto clock-in triggered by entering a job site.',
        color: 'success',
    },
    {
        icon: 'truck-fast-outline',
        title: 'Travel',
        description: 'Time logged between sites.',
        color: 'neutral',
    },
    {
        icon: 'invoice-text-edit',
        title: 'Manual',
        description: 'Entry created or edited by hand.',
        color: 'warning',
    },
    {
        icon: 'coffee-outline',
        title: 'On break',
        description: 'Currently on a break.',
        color: 'warning',
    },
    {
        icon: 'car-outline',
        title: 'Between jobs',
        description:
            'Tracked time earlier today but no active entry — likely driving off-site between locations.',
        color: 'neutral',
    },
    {
        icon: 'power-sleep',
        title: 'Off the clock',
        description: 'No active time entry right now.',
        color: 'mutedText',
    },
]

function MarkerSwatch({
    variant,
    movementIcon,
}: {
    variant: UserMarkerVariant
    movementIcon: string
}) {
    const semantic = useSemanticColors()
    const visual = getMarkerVisual(
        variant,
        movementIcon,
        variant === 'idle' ? semantic.warning : SAMPLE_USER_COLOR,
        semantic.warning
    )
    return (
        <View
            style={[
                styles.markerSwatch,
                {
                    width: MARKER_SIZE,
                    height: MARKER_SIZE,
                    borderRadius: MARKER_SIZE / 2,
                    backgroundColor: visual.bg,
                    borderColor: visual.border,
                    borderStyle: visual.dashed ? 'dashed' : 'solid',
                    borderWidth: visual.filled ? 0 : 2,
                },
            ]}
        >
            <Icon source={visual.glyph} size={Math.round(MARKER_SIZE * 0.6)} color={visual.fg} />
        </View>
    )
}

function MarkerRow({ entry }: { entry: MarkerEntry }) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    return (
        <View style={styles.row}>
            <MarkerSwatch variant={entry.variant} movementIcon={entry.movementIcon} />
            <View style={styles.rowText}>
                <Text
                    variant="labelLarge"
                    style={[styles.rowTitle, { color: theme.colors.onSurface }]}
                >
                    {entry.title}
                </Text>
                <Text variant="bodySmall" style={{ color: semantic.mutedText, lineHeight: 18 }}>
                    {entry.description}
                </Text>
            </View>
        </View>
    )
}

function EntryIconSwatch({ icon, color }: { icon: string; color: string }) {
    return (
        <View style={styles.entrySwatch}>
            <Icon source={icon} size={22} color={color} />
        </View>
    )
}

function EntryIconRowItem({ row }: { row: EntryIconRow }) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    const colorMap = {
        info: semantic.info,
        success: semantic.success,
        warning: semantic.warning,
        neutral: semantic.neutral,
        mutedText: semantic.mutedText,
        moonlight: MOONLIGHT_COLOR,
    } as const
    return (
        <View style={styles.row}>
            <EntryIconSwatch icon={row.icon} color={colorMap[row.color]} />
            <View style={styles.rowText}>
                <Text
                    variant="labelLarge"
                    style={[styles.rowTitle, { color: theme.colors.onSurface }]}
                >
                    {row.title}
                </Text>
                <Text variant="bodySmall" style={{ color: semantic.mutedText, lineHeight: 18 }}>
                    {row.description}
                </Text>
            </View>
        </View>
    )
}

function SectionHeading({ children }: { children: string }) {
    const semantic = useSemanticColors()
    return (
        <Text variant="labelSmall" style={[styles.sectionHeading, { color: semantic.mutedText }]}>
            {children}
        </Text>
    )
}

export function MonitorLegend({ visible, onDismiss }: MonitorLegendProps) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    useDismissOnEscape(visible, onDismiss)

    if (!visible) return null

    return (
        <Portal>
            <Modal
                visible
                onDismiss={onDismiss}
                contentContainerStyle={[
                    styles.container,
                    {
                        backgroundColor: theme.colors.surface,
                        borderColor: semantic.divider,
                    },
                ]}
            >
                <View style={[styles.header, { borderBottomColor: semantic.divider }]}>
                    <View style={styles.headerTitleRow}>
                        <Icon
                            source="information-outline"
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                        />
                        <Text
                            variant="titleMedium"
                            style={{ color: theme.colors.onSurface, fontWeight: '600' }}
                        >
                            Monitor legend
                        </Text>
                    </View>
                    <Button title="✕" onPress={onDismiss} mode="text" compact />
                </View>

                <ScrollView
                    style={styles.scroll}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator
                >
                    <SectionHeading>MAP MARKERS</SectionHeading>
                    <View style={styles.section}>
                        {MARKER_ENTRIES.map(entry => (
                            <MarkerRow key={entry.variant} entry={entry} />
                        ))}
                    </View>

                    <View
                        style={[styles.divider, { backgroundColor: semantic.divider }]}
                        accessibilityElementsHidden
                    />

                    <SectionHeading>STATUS ICONS</SectionHeading>
                    <View style={styles.section}>
                        {ENTRY_ICON_ROWS.map(row => (
                            <EntryIconRowItem key={row.icon} row={row} />
                        ))}
                    </View>

                    <Text
                        variant="bodySmall"
                        style={[styles.footnote, { color: semantic.mutedText }]}
                    >
                        Movement glyphs (truck, walk, run, bike) reflect the worker's live movement
                        type when available; the marker shape and color carry the operational
                        meaning.
                    </Text>
                </ScrollView>
            </Modal>
        </Portal>
    )
}

const styles = StyleSheet.create({
    container: {
        alignSelf: 'center',
        width: '100%',
        maxWidth: 460,
        maxHeight: '85%',
        marginHorizontal: 20,
        borderRadius: 16,
        borderWidth: StyleSheet.hairlineWidth,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 20,
        paddingRight: 8,
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    headerTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    scroll: {
        flexGrow: 0,
    },
    scrollContent: {
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 20,
    },
    sectionHeading: {
        letterSpacing: 1.2,
        fontWeight: '700',
        marginBottom: 10,
    },
    section: {
        gap: 14,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 14,
    },
    markerSwatch: {
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
        flexShrink: 0,
    },
    entrySwatch: {
        width: MARKER_SIZE,
        height: MARKER_SIZE,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 2,
        flexShrink: 0,
    },
    rowText: {
        flex: 1,
        gap: 2,
        minWidth: 0,
    },
    rowTitle: {
        fontWeight: '600',
    },
    divider: {
        height: StyleSheet.hairlineWidth,
        marginVertical: 18,
    },
    footnote: {
        marginTop: 16,
        fontStyle: 'italic',
        lineHeight: 18,
    },
})
